import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { axiosApi } from '@/api/axios';
import {
    AuthResponse,
    SendCodeResponse,
    SignInBody,
    SignUpBody,
    SignUpResponse,
} from '@/api/types';
import { useAuthStore, useChosenNumberStore } from '@/store';

export const useSignIn = () => {
    const { setToken, setIsAuth } = useAuthStore();
    return useMutation({
        mutationKey: ['sign-in'],
        mutationFn: async (data: SignInBody) => {
            const { data: resData, status } = await axiosApi.post<AuthResponse>(
                'auth/login',
                data,
                {
                    validateStatus: status => status < 500 && status !== 401,
                }
            );

            const accessToken = resData.data?.token;
            if (status === 200 && accessToken) {
                localStorage.setItem('token', accessToken);
                setToken(accessToken);
                setIsAuth(true);
            }

            if (status === 400) {
                return resData.errors;
            }
        },
    });
};

export const useLogOut = () => {
    const navigate = useNavigate();
    const { token, setToken, setIsAuth } = useAuthStore();
    const { clearOnUnmount } = useChosenNumberStore();
    const { mutateAsync: refresh } = useRefresh();
    const queryClient = useQueryClient();
    const logOutHandler = () => {
        queryClient.removeQueries({ queryKey: ['user'] });
        localStorage.removeItem('token');
        setToken('');
        setIsAuth(false);
        clearOnUnmount();
        navigate('/');
    };

    return useMutation({
        mutationFn: async () => {
            const { status } = await axiosApi.post('auth/logout', null, {
                headers: { Authorization: `Bearer ${token}` },
                validateStatus: status => status < 500 && status !== 401,
            });

            if (status === 200) {
                logOutHandler();
            } else {
                await refresh();
                await axiosApi.post('auth/logout', null, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                logOutHandler();
            }
        },
    });
};

export const refresh = async () => {
    const { setToken, setIsAuth, token } = useAuthStore.getState();

    if (token) {
        try {
            const { data: refreshData, status } =
                await axiosApi.post<AuthResponse>('auth/token/refresh');

            if (status === 200) {
                const jwtToken = refreshData.data?.token;
                if (jwtToken) {
                    localStorage.setItem('token', jwtToken);
                    setToken(jwtToken);
                    setIsAuth(true);
                    return refreshData;
                } else {
                    throw new Error('No token');
                }
            }
        } catch (err) {
            console.log(err);
            localStorage.removeItem('token');
            setToken('');
            setIsAuth(false);
        }
    }
};

export const useRefresh = () => {
    return useMutation({
        mutationFn: refresh,
    });
};

export const useRequestCode = () => {
    return useMutation({
        mutationFn: async (msisdn: number) => {
            const { data, status } = await axiosApi.post<SendCodeResponse>(
                'auth/send-code',
                { msisdn },
                { validateStatus: status => status < 500 }
            );
            if (status === 200 && data.data) {
                localStorage.setItem('timestamp', String(data.data.resendCooldown) + '000');
            }

            if (status === 400) {
                return data?.errors;
            }

            if (status === 429) {
                const targetError = data.errors?.find(err => err.type === 'cooldown');
                localStorage.setItem('timestamp', String(targetError?.data.resendCooldown) + '000');
                return data?.errors;
            }
        },
    });
};

export const useSignUp = () => {
    return useMutation({
        mutationFn: async (data: SignUpBody) => {
            const { data: signUpResData, status } = await axiosApi.post<SignUpResponse>(
                'auth/register',
                data,
                {
                    validateStatus: status => status < 500,
                }
            );

            if (status === 200) {
                localStorage.setItem('timestamp', String(signUpResData.data?.resendCooldown));
            } else {
                return signUpResData.errors;
            }
        },
    });
};
